import { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import {
  Page,
  TableContainer,
  Button,
  TableHeader,
  SearchForm,
  snackbar,
} from '@occc-app/design-system';
import { useIsUserPermittedTo, Feature } from '@occc/utils-access-control';
import { useSearchParams } from '@occc-app/hooks/useSearchParams';
import { usePaginationParams } from '@occc-app/hooks/usePaginationParams';
import { useDeleteKioskAssetMutation } from '@occc/graphql';

import { AssetsListTable } from './assets-list.table';

import { useGetKioskAssets } from '../../hooks/use-get-kiosk-assets.hook';
import { routeCreator } from '../../touchpoint.router';
import { AssetDeleteConfirmDialog } from '../../components/asset-delete-confirm-dialog.component';
import {
  ASSET_DELETE_ERROR_MESSAGE,
  ASSET_DELETE_SUCCESS_MESSAGE,
} from '../../touchpoint.constants';

const PAGE_TITLE = 'Assets';

export const AssetsListPage = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useSearchParams();
  const [paginationInfo, onPageChange] = usePaginationParams();

  const isUserPermittedToCreateAsset = useIsUserPermittedTo(Feature.TOUCHPOINT_ASSET_CREATE);

  const [assetIdToDelete, setAssetIdToDelete] = useState<string | null>(null);

  const [deleteKioskAsset, { loading: loadingDeleteKioskAsset }] = useDeleteKioskAssetMutation();

  const onConfirmHandler = useCallback(async () => {
    if (!assetIdToDelete) {
      return;
    }

    await deleteKioskAsset({
      variables: { id: assetIdToDelete },
      onCompleted: () => {
        snackbar.success(ASSET_DELETE_SUCCESS_MESSAGE);
        setAssetIdToDelete(null);
      },
      onError: () => {
        snackbar.error(ASSET_DELETE_ERROR_MESSAGE);
        setAssetIdToDelete(null);
      },
      refetchQueries: ['kioskAssets'],
    });
  }, [deleteKioskAsset, assetIdToDelete]);

  const onCloseHandler = useCallback(() => {
    setAssetIdToDelete(null);
  }, []);

  const assetFilter = useMemo(() => {
    return {
      searchTerm: search || undefined,
    };
  }, [search]);

  const { loading, error, data, paginationProps } = useGetKioskAssets({
    filter: assetFilter,
    paginationInfo,
    onPageChange,
  });

  const assetToDelete = useMemo(() => {
    return data?.find((asset) => asset.id === assetIdToDelete);
  }, [data, assetIdToDelete]);

  const actions = isUserPermittedToCreateAsset && (
    <Button
      color='secondary'
      variant='contained'
      component={Link}
      to={routeCreator.assetsCreate()}
      role='button'
    >
      Add New Asset
    </Button>
  );

  const renderActions = () => {
    return (
      <SearchForm
        onSubmit={(values) => {
          setSearch(values.search);
        }}
        initialValues={{ search }}
      />
    );
  };

  const navigateToAssetDetails = useCallback(
    (assetId: string) => {
      navigate(routeCreator.assetsDetails(assetId));
    },
    [navigate],
  );

  const handleEdit = useCallback(
    (assetId: string) => {
      navigate(routeCreator.assetsUpdate(assetId));
    },
    [navigate],
  );

  const handleDelete = useCallback((assetId: string) => {
    setAssetIdToDelete(assetId);
  }, []);

  return (
    <Page
      browserTabTitle={PAGE_TITLE}
      actions={actions}
      pageHeaderProps={{
        title: PAGE_TITLE,
        subtitle:
          'Upload and manage image assets for your in-store ads. Use this page to add new visuals or browse existing ones to be used in your campaigns.',
      }}
    >
      <TableContainer>
        <TableHeader title={PAGE_TITLE} actions={renderActions()} />
        <AssetsListTable
          assets={data}
          loading={loading}
          error={error}
          paginationProps={paginationProps}
          onItemClick={navigateToAssetDetails}
          onView={navigateToAssetDetails}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      </TableContainer>

      {assetIdToDelete && (
        <AssetDeleteConfirmDialog
          onClose={onCloseHandler}
          onConfirm={onConfirmHandler}
          loading={loadingDeleteKioskAsset}
          assetName={assetToDelete?.name}
        />
      )}
    </Page>
  );
};
